import React from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { FiUsers, FiUser, FiTool, FiPackage, FiTruck, FiChevronRight, FiBriefcase } from 'react-icons/fi';

const Structure = () => {
  // Static organization data
  const organizationData = {
    management: [
      {
        name: "William Luston",
        position: "Owner",
        department: "Management",
        icon: <FiUser className="text-white" size={24} />,
        level: 1,
        color: "bg-primary"
      }
    ],
    adminTeam: [
      {
        name: "Anggia Juwita",
        position: "Admin Marketing",
        department: "Admin",
        icon: <FiUsers className="text-white" size={24} />,
        level: 2,
        color: "bg-blue-500"
      },
      {
        name: "Lintang Shalsabilla",
        position: "Admin Finance",
        department: "Admin",
        icon: <FiUsers className="text-white" size={24} />,
        level: 2,
        color: "bg-blue-500"
      },
      {
        name: "Agustina Juni",
        position: "Admin Gudang",
        department: "Admin",
        icon: <FiPackage className="text-white" size={24} />,
        level: 2,
        color: "bg-blue-500"
      }
    ],
    technicalTeam: [
      {
        name: "Febri Adi Kusuma",
        position: "Kepala Teknisi",
        department: "Technical",
        icon: <FiTool className="text-white" size={24} />,
        level: 2,
        color: "bg-green-500"
      },
      {
        name: "Dedi",
        position: "Teknisi",
        department: "Technical",
        icon: <FiTool className="text-white" size={24} />,
        level: 3,
        color: "bg-green-600"
      },
      {
        name: "Tarmizi",
        position: "Teknisi",
        department: "Technical",
        icon: <FiTool className="text-white" size={24} />,
        level: 3,
        color: "bg-green-600"
      },
      {
        name: "Irfan Setiawan",
        position: "Helper Teknisi",
        department: "Technical",
        icon: <FiTool className="text-white" size={24} />,
        level: 4,
        color: "bg-green-700"
      }
    ],
    supportTeam: [
      {
        name: "Andri Priyoga",
        position: "Helper Gudang",
        department: "Support",
        icon: <FiPackage className="text-white" size={24} />,
        level: 3,
        color: "bg-yellow-500"
      },
      {
        name: "Zanki",
        position: "Kurir",
        department: "Support",
        icon: <FiTruck className="text-white" size={24} />,
        level: 3,
        color: "bg-yellow-500"
      }
    ]
  };

  // Component for a single staff card
  const StaffCard = ({ staff }) => (
    <div className={`rounded-lg shadow-md overflow-hidden transition-all duration-300 transform hover:scale-105 hover:shadow-lg`}>
      <div className={`${staff.color} p-5 flex items-center`}>
        <div className="p-3 bg-white bg-opacity-20 rounded-full mr-4 flex-shrink-0">
          {staff.icon}
        </div>
        <div className="text-white overflow-hidden">
          <h3 className="font-bold text-lg truncate">{staff.name}</h3>
          <p className="text-sm opacity-90 truncate">{staff.position}</p>
        </div>
      </div>
      <div className="bg-white p-3">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span className="py-1 px-3 bg-gray-100 rounded-full truncate max-w-[70%]">{staff.department}</span>
          <div className="flex items-center">
            <FiChevronRight size={14} className="text-gray-400 mr-1" />
            <span className="font-medium">L{staff.level}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Grid layout for the organization chart
  return (
    <DashboardLayout userRole="owner" userName="Owner">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex flex-col items-center mb-12">
          <h1 className="text-3xl font-bold text-secondary mb-3 text-center">Struktur Organisasi</h1>
          <div className="h-1 w-24 bg-primary rounded-full mb-2"></div>
          <p className="text-gray-600 text-center max-w-2xl">
            Struktur organisasi X-Signature menggambarkan hubungan dan hirarki antara berbagai posisi dan departemen dalam perusahaan.
          </p>
        </div>

        {/* Management - Top Level */}
        <div className="flex justify-center mb-6">
          <div className="w-full max-w-sm">
            <StaffCard staff={organizationData.management[0]} />
          </div>
        </div>

        {/* Connector */}
        <div className="flex justify-center mb-2">
          <div className="h-10 w-0.5 bg-gray-300"></div>
        </div>

        {/* Second Level - Department Heads */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {organizationData.adminTeam.map((staff, index) => (
            <div key={`admin-${index}`} className="relative pt-4">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-0.5 bg-gray-300"></div>
              <StaffCard staff={staff} />
            </div>
          ))}
          <div className="relative pt-4">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-0.5 bg-gray-300"></div>
            <StaffCard staff={organizationData.technicalTeam[0]} />
          </div>
        </div>

        {/* Third Level - Staff */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Helper Gudang under Admin Gudang */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-blue-500 flex items-center border-b pb-2">
              <FiPackage className="mr-2" /> Tim Admin Support
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="relative pt-4 ml-8 sm:ml-0">
                <div className="absolute top-0 left-8 sm:left-1/2 sm:-translate-x-1/2 h-4 w-0.5 bg-gray-300"></div>
                <StaffCard staff={organizationData.supportTeam[0]} />
              </div>
              <div className="ml-8 sm:ml-0">
                <div className="opacity-0">Spacer</div>
              </div>
            </div>
          </div>

          {/* Technical Team */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-green-500 flex items-center border-b pb-2">
              <FiTool className="mr-2" /> Tim Teknikal
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {organizationData.technicalTeam
                .filter(staff => staff.level === 3)
                .map((staff, index) => (
                  <div key={`tech-${index}`} className="relative pt-4 ml-8 sm:ml-0">
                    <div className="absolute top-0 left-8 sm:left-1/2 sm:-translate-x-1/2 h-4 w-0.5 bg-gray-300"></div>
                    <StaffCard staff={staff} />
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Fourth Level - Helpers & Kurir */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Empty space for left column */}
          <div className="lg:block hidden"></div>

          {/* Technical Team Helpers */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-yellow-500 flex items-center border-b pb-2">
              <FiTruck className="mr-2" /> Tim Support & Helper
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="relative pt-4 ml-16 sm:ml-8">
                <div className="absolute top-0 left-8 h-4 w-0.5 bg-gray-300"></div>
                <StaffCard staff={organizationData.technicalTeam[3]} />
              </div>
              <div className="relative pt-4 ml-16 sm:ml-8">
                <div className="absolute top-0 left-8 h-4 w-0.5 bg-gray-300"></div>
                <StaffCard staff={organizationData.supportTeam[1]} />
              </div>
            </div>
          </div>
        </div>

        {/* Organization Chart Table View */}
        <div className="bg-white rounded-lg shadow-lg p-6 overflow-hidden mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
            <FiBriefcase className="mr-2 text-primary" />
            <span>Tabel Struktur Organisasi</span>
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posisi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Departemen
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Management */}
                {organizationData.management.map((staff, index) => (
                  <tr key={`table-management-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`${staff.color} p-2 rounded-full mr-3`}>
                          {React.cloneElement(staff.icon, { size: 16 })}
                        </div>
                        <div className="font-medium text-gray-900">{staff.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {staff.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-primary bg-opacity-10 text-primary">
                        {staff.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      Level {staff.level}
                    </td>
                  </tr>
                ))}

                {/* Admin Team */}
                {organizationData.adminTeam.map((staff, index) => (
                  <tr key={`table-admin-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`${staff.color} p-2 rounded-full mr-3`}>
                          {React.cloneElement(staff.icon, { size: 16 })}
                        </div>
                        <div className="font-medium text-gray-900">{staff.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {staff.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-600">
                        {staff.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      Level {staff.level}
                    </td>
                  </tr>
                ))}

                {/* Technical Team */}
                {organizationData.technicalTeam.map((staff, index) => (
                  <tr key={`table-tech-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`${staff.color} p-2 rounded-full mr-3`}>
                          {React.cloneElement(staff.icon, { size: 16 })}
                        </div>
                        <div className="font-medium text-gray-900">{staff.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {staff.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600">
                        {staff.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      Level {staff.level}
                    </td>
                  </tr>
                ))}

                {/* Support Team */}
                {organizationData.supportTeam.map((staff, index) => (
                  <tr key={`table-support-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`${staff.color} p-2 rounded-full mr-3`}>
                          {React.cloneElement(staff.icon, { size: 16 })}
                        </div>
                        <div className="font-medium text-gray-900">{staff.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {staff.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-600">
                        {staff.department}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      Level {staff.level}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Structure; 